/* eslint-disable -- UX wireframe import; not held to app lint rules */
import React,{useState,useEffect,useRef} from "react";
import { Link } from "react-router-dom";
import WizardChrome from "../../shared/WizardChrome";
import ViewportFrame from "../../shared/ViewportFrame";
import { canvasHeightForViewport } from "../../shared/layoutTokens";
import { WIZARD_TABS } from "../../shared/wizardTabs";
import { InspectProvider } from "../../shared/Inspector";
import { SAShell } from "../../shared/saWireframeShells";

// ─── BRAND TOKENS (exact copy from ZiniHub) ───────────────────────────────────
const T = {
  brand:"#5e4ce2",brandHover:"#5046d4",brandSoft:"rgba(94,76,226,0.12)",brandBorder:"rgba(94,76,226,0.28)",
  cyan:"#06b6d4",cyanSoft:"rgba(6,182,212,0.1)",teal:"#14b8a6",tealSoft:"rgba(20,184,166,0.1)",
  amber:"#f59e0b",amberSoft:"rgba(245,158,11,0.12)",coral:"#f97316",coralSoft:"rgba(249,115,22,0.1)",
  green:"#22c55e",greenSoft:"rgba(34,197,94,0.1)",red:"#ef4444",redSoft:"rgba(239,68,68,0.1)",violet:"#7c3aed",
  lCanvas:"#f8fafc",lSurface:"#ffffff",lMuted:"#f1f5f9",lBorder:"rgba(51,65,85,0.1)",lBorderMd:"rgba(51,65,85,0.18)",
  lText:"#0f172a",lTextSec:"#475569",lTextMuted:"#94a3b8",
  dCanvas:"#0a0d12",dSurface:"#111318",dElevated:"#181c23",dMuted:"#1e2330",
  dBorder:"rgba(255,255,255,0.08)",dBorderMd:"rgba(255,255,255,0.14)",
  dText:"#f1f5f9",dTextSec:"#b0bec5",dTextMuted:"#64748b",
  fast:"150ms",mid:"220ms",slow:"320ms",
  ease:"cubic-bezier(0.4,0,0.2,1)",spring:"cubic-bezier(0.34,1.56,0.64,1)",
  rsm:"6px",rmd:"10px",rlg:"16px",rxl:"24px",
};
function useTheme(dark){
  return{
    bg:dark?T.dCanvas:T.lCanvas,surface:dark?T.dSurface:T.lSurface,
    elevated:dark?T.dElevated:T.lSurface,muted:dark?T.dMuted:T.lMuted,
    border:dark?T.dBorder:T.lBorder,borderMd:dark?T.dBorderMd:T.lBorderMd,
    text:dark?T.dText:T.lText,textSec:dark?T.dTextSec:T.lTextSec,textMuted:dark?T.dTextMuted:T.lTextMuted,
    sidebar:dark?"#0d1017":T.lSurface,
    dark,tr:`all ${T.mid} ${T.ease}`,trSlow:`all ${T.slow} ${T.ease}`,
  };
}

// ─── PRIMITIVES (same pattern as ZiniHub) ─────────────────────────────────────
const Row=({children,gap=8,mb=0,align="center",wrap=false})=>
  <div style={{display:"flex",gap,alignItems:align,marginBottom:mb,flexWrap:wrap?"wrap":"nowrap"}}>{children}</div>;
const Box=({w="100%",h=12,r=6,bg,style={}})=>
  <div style={{width:w,height:h,borderRadius:r,background:bg||"rgba(128,128,128,0.12)",flexShrink:0,...style}}/>;
const MockBtn=({label,type="outline",w,small,style={}})=>{
  const s={primary:{background:T.brand,color:"#fff",border:"none"},outline:{background:"transparent",color:"rgba(128,128,128,0.65)",border:"1px solid rgba(128,128,128,0.2)"},danger:{background:"transparent",color:T.red,border:`1px solid ${T.red}44`},ghost:{background:T.brandSoft,color:T.brand,border:`1px solid ${T.brandBorder}`}};
  return <div style={{height:small?26:34,padding:"0 12px",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:small?10:12,fontWeight:type==="primary"?"500":"400",width:w,flexShrink:0,cursor:"default",...(s[type]||s.outline),...style}}>{label}</div>;
};
const MockField=({label,val,hint,req,w="100%"})=>(
  <div style={{width:w,marginBottom:10}}>
    <div style={{fontSize:10,color:"rgba(128,128,128,0.6)",marginBottom:3}}>{req&&<span style={{color:T.red}}>* </span>}{label}</div>
    <div style={{height:34,background:"rgba(128,128,128,0.07)",border:"1px solid rgba(128,128,128,0.15)",borderRadius:7,padding:"0 10px",display:"flex",alignItems:"center"}}>
      {val&&<span style={{fontSize:11,color:"rgba(128,128,128,0.65)"}}>{val}</span>}
    </div>
    {hint&&<div style={{fontSize:9,color:"rgba(128,128,128,0.4)",marginTop:2}}>{hint}</div>}
  </div>
);
const MockBadge=({label,type="neutral"})=>{
  const c={active:{bg:"rgba(34,197,94,0.15)",t:T.green},expiring:{bg:"rgba(245,158,11,0.15)",t:T.amber},expired:{bg:"rgba(239,68,68,0.15)",t:T.red},trial:{bg:"rgba(245,158,11,0.12)",t:T.amber},starter:{bg:"rgba(6,182,212,0.12)",t:T.cyan},professional:{bg:"rgba(94,76,226,0.12)",t:T.brand},enterprise:{bg:"rgba(124,58,237,0.12)",t:T.violet},neutral:{bg:"rgba(128,128,128,0.1)",t:"rgba(128,128,128,0.55)"}}[type.toLowerCase()]||{bg:"rgba(128,128,128,0.1)",t:"rgba(128,128,128,0.55)"};
  return <span style={{background:c.bg,color:c.t,borderRadius:5,padding:"2px 7px",fontSize:10,fontWeight:"500",flexShrink:0,border:`1px solid ${c.t}22`}}>{label}</span>;
};

// Browser frame wrapper (same as ZiniHub WF)
function WF({dark,viewport="desktop",slim=false,noPad=false,children}){
  const th=useTheme(dark);
  const w=viewport==="mobile"?"375px":"100%";
  return(
    <div style={{width:`min(100%,${w})`,background:dark?T.dElevated:T.lMuted,borderRadius:T.rlg,border:`1px solid ${th.border}`,overflow:"hidden",transition:`width ${T.slow} ${T.ease}`}}>
      <div style={{display:"flex",alignItems:"center",gap:5,padding:"6px 10px",background:dark?"rgba(0,0,0,0.3)":"rgba(0,0,0,0.04)",borderBottom:`1px solid ${th.border}`}}>
        {["#ff5f57","#ffbd2e","#28c840"].map(c=><div key={c} style={{width:6,height:6,borderRadius:"50%",background:c}}/>)}
        <div style={{flex:1,height:14,background:dark?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)",borderRadius:3,margin:"0 5px",paddingLeft:7,display:"flex",alignItems:"center"}}>
          <span style={{fontSize:8,color:dark?"rgba(255,255,255,0.3)":"rgba(0,0,0,0.25)",fontFamily:"monospace"}}>superadmin.ziniz.online</span>
        </div>
        <span style={{fontSize:8,color:dark?"rgba(255,255,255,0.25)":"rgba(0,0,0,0.2)",background:dark?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)",borderRadius:3,padding:"1px 5px"}}>{viewport}</span>
      </div>
      <div style={{padding:noPad?0:slim?"10px 12px":"14px 16px"}}>{children}</div>
    </div>
  );
}

// ─── SCREEN WIREFRAMES ────────────────────────────────────────────────────────
function ScreenWF({id,dark,viewport,slim}){
  const th=useTheme(dark);
  const mob=viewport==="mobile";

  const screens={
    // ── LOGIN ──
    "sa-login":()=>(
      <WF dark={dark} viewport={viewport} slim={slim} noPad>
        <div style={{display:"flex",minHeight:slim?200:320,overflow:"hidden"}}>
          {/* Brand panel — fixed width, no flex shrink */}
          {!mob&&<div style={{width:160,flexShrink:0,background:`linear-gradient(135deg,${T.brand} 0%,${T.violet} 100%)`,padding:"16px 14px",display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"flex",alignItems:"center",gap:5}}>
              <div style={{width:18,height:18,borderRadius:5,background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,flexShrink:0}}>⚡</div>
              <span style={{fontSize:9,fontWeight:600,color:"#fff",lineHeight:1.2}}>ZiniZ Super Admin</span>
            </div>
            <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"center",gap:8}}>
              <div style={{fontSize:13,fontWeight:700,color:"#fff",lineHeight:1.3}}>ZiniZ Control<br/>Centre</div>
              <div style={{fontSize:9,color:"rgba(255,255,255,0.7)",lineHeight:1.5}}>God-mode access for all ZiniZ umbrella apps.</div>
              {[["🔐","Role-based access"],["💳","Subscription control"],["📦","Plan management"]].map(([i,l])=>(
                <div key={l} style={{display:"flex",alignItems:"center",gap:5,background:"rgba(255,255,255,0.1)",borderRadius:5,padding:"4px 7px"}}>
                  <span style={{fontSize:9,flexShrink:0}}>{i}</span>
                  <span style={{fontSize:9,color:"rgba(255,255,255,0.85)",lineHeight:1.2}}>{l}</span>
                </div>
              ))}
            </div>
            <div style={{fontSize:7,color:"rgba(255,255,255,0.35)"}}>ZiniZ Super Admin ©2026</div>
          </div>}
          {/* Form area */}
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:mob?"14px":"18px",background:dark?T.dElevated:"#f8fafc",minWidth:0}}>
            <div style={{width:"100%",maxWidth:240}}>
              {mob&&<div style={{textAlign:"center",marginBottom:14}}><div style={{width:28,height:28,borderRadius:8,background:T.brand,margin:"0 auto 8px",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>⚡</div><div style={{fontSize:12,fontWeight:600,color:th.text}}>ZiniZ Super Admin</div></div>}
              {!mob&&<div style={{fontSize:13,fontWeight:600,color:th.text,marginBottom:14}}>Login to Super Admin</div>}
              <MockField label="Mobile / Username" val="+91 ··········" req/>
              <MockField label="Password" val="••••••••" req/>
              <MockBtn label="Login" type="primary" w="100%"/>
              <div style={{textAlign:"center",fontSize:8,color:th.textMuted,marginTop:7,lineHeight:1.5}}>Role check: super_admin required on ZiniHub account</div>
            </div>
          </div>
        </div>
      </WF>
    ),

    // ── SUBSCRIPTIONS ──
    "subscriptions":()=>(
      <WF dark={dark} viewport={viewport} slim={slim}>
        <SAShell dark={dark} active="subscriptions" viewport={viewport}>
          <div style={{fontSize:12,fontWeight:600,color:th.text,marginBottom:10}}>Subscriptions</div>
          {/* Stats */}
          <div style={{display:"grid",gridTemplateColumns:mob?"1fr 1fr":"repeat(4,1fr)",gap:6,marginBottom:10}}>
            {[["Total","5",T.brand],["Active","3",T.green],["Expiring","1",T.amber],["Expired","1",T.red]].map(([l,v,c])=>(
              <div key={l} style={{background:dark?T.dSurface:T.lSurface,borderRadius:T.rmd,border:`1px solid ${th.border}`,padding:"8px 10px"}}>
                <div style={{fontSize:8,color:th.textMuted,marginBottom:2}}>{l}</div>
                <div style={{fontSize:18,fontWeight:700,color:c}}>{v}</div>
              </div>
            ))}
          </div>
          {/* Search */}
          <div style={{display:"flex",gap:6,marginBottom:8}}>
            <div style={{flex:1,height:28,background:dark?T.dSurface:T.lSurface,borderRadius:T.rmd,border:`1px solid ${th.border}`,padding:"0 8px",display:"flex",alignItems:"center"}}><span style={{fontSize:9,color:th.textMuted}}>🔍 Search hub, owner, mobile…</span></div>
            {!mob&&<div style={{height:28,padding:"0 10px",background:dark?T.dSurface:T.lSurface,borderRadius:T.rmd,border:`1px solid ${th.border}`,display:"flex",alignItems:"center",fontSize:9,color:th.textSec}}>All Status ▾</div>}
          </div>
          {/* Table header */}
          <div style={{background:th.muted,borderRadius:`${T.rmd} ${T.rmd} 0 0`,border:`1px solid ${th.border}`,padding:"5px 10px"}}>
            <div style={{display:"grid",gridTemplateColumns:mob?"1fr 1fr 40px":"2fr 1.5fr 80px 70px 90px 70px 44px",gap:4,fontSize:8,fontWeight:600,color:th.textMuted,letterSpacing:"0.06em"}}>
              {(mob?["HUB","PLAN","⋯"]:["HUB","OWNER","PLAN","VALIDITY","EXPIRY","STATUS","⋯"]).map(h=><div key={h}>{h}</div>)}
            </div>
          </div>
          {/* Rows */}
          {[["Hub22 – Bengaluru Foods","hub22","Ravi Kumar","professional","Monthly","2026-08-12","active"],
            ["Craft Corner Studio","hub45","Priya Mehta","starter","Quarterly","2026-05-18","expiring"],
            ["TechGuru Electronics","hub99","Suresh Nair","trial","Monthly","2026-05-20","expired"]].map(([name,id,owner,plan,validity,exp,status])=>(
            <div key={id} style={{display:"grid",gridTemplateColumns:mob?"1fr 1fr 40px":"2fr 1.5fr 80px 70px 90px 70px 44px",gap:4,padding:"6px 10px",borderBottom:`1px solid ${th.border}`,borderLeft:`1px solid ${th.border}`,borderRight:`1px solid ${th.border}`,alignItems:"center"}}>
              <div><div style={{fontSize:9,fontWeight:600,color:th.text}}>{name}</div><div style={{fontSize:8,color:th.textMuted}}>{id}</div></div>
              {!mob&&<div style={{fontSize:9,color:th.textSec}}>{owner}</div>}
              <MockBadge label={plan.charAt(0).toUpperCase()+plan.slice(1)} type={plan}/>
              {!mob&&<div style={{fontSize:9,color:th.textSec}}>{validity}</div>}
              {!mob&&<div><div style={{fontSize:9,color:th.text}}>{exp}</div><div style={{fontSize:8,color:status==="expired"?T.red:status==="expiring"?T.amber:th.textMuted}}>{status==="expired"?"Expired":status==="expiring"?"⚠ 2d left":"84d left"}</div></div>}
              {!mob&&<MockBadge label={status==="expiring"?"Expiring Soon":status.charAt(0).toUpperCase()+status.slice(1)} type={status==="expiring"?"expiring":status}/>}
              <div style={{fontSize:13,color:th.textMuted,cursor:"default",textAlign:"center"}}>⋯</div>
            </div>
          ))}
        </SAShell>
      </WF>
    ),

    // ── EXTEND VALIDITY MODAL ──
    "modal-extend":()=>(
      <WF dark={dark} viewport={viewport} slim={slim}>
        <SAShell dark={dark} active="subscriptions" viewport={viewport}>
          <div style={{position:"relative",minHeight:200}}>
            <div style={{opacity:0.3,filter:"blur(1px)",pointerEvents:"none"}}>
              <div style={{fontSize:12,fontWeight:600,color:th.text,marginBottom:8}}>Subscriptions</div>
              <Box h={8} style={{marginBottom:6}}/>
            </div>
            {/* Modal overlay */}
            <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:10,zIndex:10}}>
              <div style={{background:"#2c3149",borderLeft:`3px solid ${T.brand}`,border:"1px solid rgba(94,76,226,0.4)",borderLeftWidth:"3px",borderRadius:T.rlg,padding:"14px 16px",width:mob?"90%":280}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <div><div style={{fontSize:11,fontWeight:700,color:"#f1f5f9"}}>Extend Validity</div><div style={{fontSize:9,color:"#94a3b8"}}>Hub22 – Bengaluru Foods · Ravi Kumar</div></div>
                  <span style={{color:"#64748b",fontSize:14}}>×</span>
                </div>
                <div style={{background:"rgba(94,76,226,0.1)",borderRadius:T.rmd,padding:"6px 9px",marginBottom:10,display:"flex",gap:10,fontSize:9,color:"#c4b5fd"}}>
                  <span>Plan: <b>Professional</b></span><span>Expires: <b>2026-08-12</b></span>
                </div>
                <div style={{fontSize:9,color:"#64748b",marginBottom:4}}>* Days to extend (max 365)</div>
                <div style={{height:28,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(100,116,139,0.3)",borderRadius:T.rmd,marginBottom:6,padding:"0 8px",display:"flex",alignItems:"center"}}><span style={{fontSize:10,color:"#94a3b8"}}>30</span></div>
                <div style={{fontSize:9,color:"#64748b",marginBottom:10}}>New expiry: <b style={{color:"#f1f5f9"}}>2026-09-11</b></div>
                <Row gap={6}><MockBtn label="Confirm Extension" type="primary" small/><MockBtn label="Cancel" small/></Row>
              </div>
            </div>
          </div>
        </SAShell>
      </WF>
    ),

    // ── CASHFREE LINK GENERATION ──
    "cashfree-form":()=>(
      <WF dark={dark} viewport={viewport} slim={slim}>
        <SAShell dark={dark} active="cashfree" viewport={viewport}>
          <div style={{fontSize:12,fontWeight:600,color:th.text,marginBottom:4}}>Generate Payment Link</div>
          <div style={{fontSize:9,color:th.textSec,marginBottom:12}}>Create a Cashfree payment link and send to a hub owner.</div>
          {/* Hub search */}
          <div style={{background:dark?T.dSurface:T.lSurface,border:`1px solid ${th.border}`,borderRadius:T.rlg,padding:"10px 12px",marginBottom:10}}>
            <div style={{fontSize:9,fontWeight:600,color:th.textMuted,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:7}}>1 · Select Hub</div>
            <MockField label="Search hub or owner" val="Hub22 – Bengaluru Foods · Ravi Kumar" req/>
            <div style={{display:"flex",gap:8,fontSize:9,color:th.textSec}}>
              <span>Mobile: <b style={{color:th.text}}>+91 98765 43210</b></span>
              <span>Plan: <MockBadge label="Professional" type="professional"/></span>
            </div>
          </div>
          {/* Plan & validity */}
          <div style={{background:dark?T.dSurface:T.lSurface,border:`1px solid ${th.border}`,borderRadius:T.rlg,padding:"10px 12px",marginBottom:10}}>
            <div style={{fontSize:9,fontWeight:600,color:th.textMuted,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:7}}>2 · Select Plan + Validity</div>
            <Row gap={8}>
              <div style={{flex:1}}>
                <div style={{fontSize:9,color:"#64748b",marginBottom:3}}>* Plan</div>
                <div style={{height:28,background:"rgba(128,128,128,0.07)",border:"1px solid rgba(128,128,128,0.15)",borderRadius:T.rmd,padding:"0 8px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span style={{fontSize:10,color:th.text}}>Professional</span><span style={{fontSize:9,color:th.textMuted}}>▾</span>
                </div>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:9,color:"#64748b",marginBottom:3}}>* Validity</div>
                <div style={{height:28,background:"rgba(128,128,128,0.07)",border:"1px solid rgba(128,128,128,0.15)",borderRadius:T.rmd,padding:"0 8px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span style={{fontSize:10,color:th.text}}>Monthly</span><span style={{fontSize:9,color:th.textMuted}}>▾</span>
                </div>
              </div>
            </Row>
            <div style={{marginTop:8,padding:"8px 10px",background:T.brandSoft,borderRadius:T.rmd,border:`1px solid ${T.brandBorder}`}}>
              <span style={{fontSize:9,color:th.textSec}}>Amount: </span>
              <span style={{fontSize:16,fontWeight:700,color:T.brand}}>₹799</span>
              <span style={{fontSize:9,color:th.textMuted,marginLeft:6}}>via Cashfree · sandbox</span>
            </div>
          </div>
          {/* Customer details */}
          <div style={{background:dark?T.dSurface:T.lSurface,border:`1px solid ${th.border}`,borderRadius:T.rlg,padding:"10px 12px",marginBottom:10}}>
            <div style={{fontSize:9,fontWeight:600,color:th.textMuted,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:7}}>3 · Customer Details (auto-filled)</div>
            <Row gap={6}>
              <MockField label="Customer name" val="Ravi Kumar" w="50%"/>
              <MockField label="Customer mobile" val="+91 98765 43210" w="50%"/>
            </Row>
            <MockField label="Customer email" val="ravi@example.com" hint="Optional — for payment receipt"/>
            <div style={{display:"flex",gap:8,fontSize:9}}>
              <div style={{flex:1}}>
                <div style={{color:"#64748b",marginBottom:3}}>Link description</div>
                <div style={{height:28,background:"rgba(128,128,128,0.07)",border:"1px solid rgba(128,128,128,0.15)",borderRadius:T.rmd,padding:"0 8px",display:"flex",alignItems:"center"}}><span style={{fontSize:9,color:th.textMuted}}>ZiniHub Professional – Monthly</span></div>
              </div>
              <div style={{flex:1}}>
                <div style={{color:"#64748b",marginBottom:3}}>Link expiry</div>
                <div style={{height:28,background:"rgba(128,128,128,0.07)",border:"1px solid rgba(128,128,128,0.15)",borderRadius:T.rmd,padding:"0 8px",display:"flex",alignItems:"center"}}><span style={{fontSize:9,color:th.textMuted}}>24 hours</span></div>
              </div>
            </div>
          </div>
          <MockBtn label="Generate Cashfree Link" type="primary" w="100%"/>
        </SAShell>
      </WF>
    ),

    // ── CASHFREE LINK GENERATED ──
    "cashfree-generated":()=>(
      <WF dark={dark} viewport={viewport} slim={slim}>
        <SAShell dark={dark} active="cashfree" viewport={viewport}>
          <div style={{fontSize:12,fontWeight:600,color:th.text,marginBottom:12}}>Generate Payment Link</div>
          {/* Success state */}
          <div style={{background:T.greenSoft,border:`1px solid rgba(34,197,94,0.3)`,borderRadius:T.rlg,padding:"12px 14px",marginBottom:10}}>
            <div style={{fontSize:11,fontWeight:600,color:T.green,marginBottom:4}}>✓ Link generated successfully</div>
            <div style={{fontSize:9,color:th.textSec,marginBottom:8}}>Cashfree sandbox · expires in 24 hours · ₹799</div>
            <div style={{background:"rgba(0,0,0,0.15)",borderRadius:T.rmd,padding:"6px 10px",marginBottom:8,fontFamily:"monospace",fontSize:8,color:T.cyan,wordBreak:"break-all"}}>
              https://payments.cashfree.com/links/ziniz_hub22_professional_17921...
            </div>
            <Row gap={6}>
              <MockBtn label="📋 Copy Link" type="primary" small/>
              <MockBtn label="📱 Send via WhatsApp" type="ghost" small/>
              <MockBtn label="New Link" small/>
            </Row>
          </div>
          {/* History */}
          <div style={{background:dark?T.dSurface:T.lSurface,border:`1px solid ${th.border}`,borderRadius:T.rlg,overflow:"hidden"}}>
            <div style={{padding:"7px 12px",borderBottom:`1px solid ${th.border}`,fontSize:10,fontWeight:600,color:th.text}}>Recent links for this hub</div>
            {[["Professional · Monthly · ₹799","2 min ago","pending"],["Starter · Annual · ₹2,999","3 days ago","paid"]].map(([desc,time,status])=>(
              <div key={desc} style={{display:"flex",gap:8,padding:"6px 12px",borderBottom:`1px solid ${th.border}`,alignItems:"center",fontSize:9}}>
                <div style={{flex:1,color:th.textSec}}>{desc}</div>
                <div style={{color:th.textMuted}}>{time}</div>
                <MockBadge label={status} type={status==="paid"?"active":"trial"}/>
              </div>
            ))}
          </div>
        </SAShell>
      </WF>
    ),

    // ── PLAN MANAGER ──
    "plan-manager":()=>(
      <WF dark={dark} viewport={viewport} slim={slim}>
        <SAShell dark={dark} active="plans" viewport={viewport}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:600,color:th.text}}>Plan Manager</div>
            <MockBtn label="+ New Plan" type="ghost" small/>
          </div>
          <Row gap={4} mb={10}>
            {["Monthly","Quarterly","Annual"].map((v,i)=>(
              <div key={v} style={{padding:"3px 10px",borderRadius:99,fontSize:9,border:`1px solid ${i===0?T.brand:th.borderMd}`,background:i===0?T.brandSoft:"transparent",color:i===0?T.brand:th.textSec,cursor:"default"}}>{v}</div>
            ))}
          </Row>
          <div style={{display:"grid",gridTemplateColumns:mob?"1fr 1fr":"repeat(4,1fr)",gap:8}}>
            {[["Trial",T.amber,"Free",0],["Starter",T.cyan,"₹299",1],["Professional",T.brand,"₹799",2],["Enterprise",T.violet,"₹1,999",3]].map(([name,col,price])=>(
              <div key={name} style={{background:dark?T.dSurface:T.lSurface,borderRadius:T.rlg,border:`1px solid ${th.border}`,borderTop:`3px solid ${col}`,padding:"8px 10px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <div style={{fontSize:10,fontWeight:700,color:col}}>{name}</div>
                  <MockBtn label="Edit" small/>
                </div>
                <div style={{fontSize:14,fontWeight:700,color:th.text,marginBottom:6}}>{price}<span style={{fontSize:8,fontWeight:400,color:th.textMuted}}>/mo</span></div>
                {[["Max Stores","1"],["Items/Store","10"],["Staff","1"],["Analytics","✗"]].map(([k,v])=>(
                  <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:8,padding:"2px 0",borderBottom:`1px solid ${th.border}`}}>
                    <span style={{color:th.textSec}}>{k}</span>
                    <span style={{fontWeight:600,color:v==="✗"?T.red:v==="✓"?T.green:th.text}}>{v}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </SAShell>
      </WF>
    ),

    // ── PLAN EDIT MODAL ──
    "plan-edit-modal":()=>(
      <WF dark={dark} viewport={viewport} slim={slim}>
        <SAShell dark={dark} active="plans" viewport={viewport}>
          <div style={{position:"relative",minHeight:220}}>
            <div style={{opacity:0.25,pointerEvents:"none",fontSize:11,color:th.text}}>Plan Manager</div>
            <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:8,zIndex:10}}>
              <div style={{background:"#2c3149",borderLeft:`3px solid ${T.brand}`,border:"1px solid rgba(94,76,226,0.4)",borderLeftWidth:"3px",borderRadius:T.rlg,padding:"12px 14px",width:mob?"92%":290}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#f1f5f9"}}>Edit Plan: Professional</div>
                  <span style={{color:"#64748b",fontSize:14}}>×</span>
                </div>
                <MockField label="Plan Name" val="Professional"/>
                <div style={{fontSize:9,fontWeight:600,color:"#f1f5f9",marginBottom:6}}>Pricing (₹)</div>
                <Row gap={5} mb={8}>
                  <MockField label="Monthly" val="799" w="33%"/>
                  <MockField label="Quarterly" val="2099" w="33%"/>
                  <MockField label="Annual" val="7999" w="33%"/>
                </Row>
                <div style={{fontSize:9,fontWeight:600,color:"#f1f5f9",marginBottom:6}}>Feature Limits</div>
                {[["Max Stores","10"],["Max Items/Store","500"],["Max Images/Item","10"],["Staff Accounts","10"]].map(([k,v])=>(
                  <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4,fontSize:9}}>
                    <span style={{color:"#94a3b8"}}>{k}</span>
                    <div style={{width:50,height:22,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(100,116,139,0.3)",borderRadius:T.rsm,display:"flex",alignItems:"center",justifyContent:"flex-end",paddingRight:6}}><span style={{fontSize:9,color:"#f1f5f9"}}>{v}</span></div>
                  </div>
                ))}
                {[["KYC Module","on"],["Orders Module","on"],["Analytics","on"]].map(([k,v])=>(
                  <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4,fontSize:9}}>
                    <span style={{color:"#94a3b8"}}>{k}</span>
                    <div style={{width:32,height:16,borderRadius:99,background:T.green,position:"relative"}}><div style={{width:12,height:12,borderRadius:"50%",background:"#fff",position:"absolute",top:2,left:18}}/></div>
                  </div>
                ))}
                <Row gap={6} style={{marginTop:10}}><MockBtn label="Save Plan" type="primary" small/><MockBtn label="Cancel" small/></Row>
              </div>
            </div>
          </div>
        </SAShell>
      </WF>
    ),
  };
  const fn=screens[id];
  const body=fn?fn():(
    <WF dark={dark} viewport={viewport} slim={slim}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:30,minHeight:slim?80:160,textAlign:"center"}}>
        <div><div style={{fontSize:24,marginBottom:8}}>📐</div><div style={{fontSize:11,color:"rgba(128,128,128,0.5)"}}>{id}</div></div>
      </div>
    </WF>
  );
  if(slim) return body;
  return (
    <div style={{ flex: 1, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
      {body}
    </div>
  );
}

// ─── DATA ─────────────────────────────────────────────────────────────────────
const SA_ROUTES = [
  {path:"/login",             comp:"LoginPage",        shell:"SAPublicShell",  auth:"public",  note:"Split layout. Left: brand panel (brand→violet gradient). Right: form. Mobile: centred card."},
  {path:"/dashboard",         comp:"SubscriptionsPage",shell:"SAShell",        auth:"sa_only", note:"Default landing. Hub search + stats + table."},
  {path:"/subscriptions",     comp:"SubscriptionsPage",shell:"SAShell",        auth:"sa_only", note:"Same as dashboard — canonical URL."},
  {path:"/plans",             comp:"PlanManagerPage",  shell:"SAShell",        auth:"sa_only", note:"Plan cards + validity toggle. Edit via modal."},
  {path:"/cashfree",          comp:"CashfreeLinksPage",shell:"SAShell",        auth:"sa_only", note:"Cashfree payment link generator. Hub search → plan/validity picker → generate → copy/WhatsApp."},
  {path:"/cashfree/history",  comp:"CashfreeHistoryPage",shell:"SAShell",      auth:"sa_only", note:"All generated links across hubs. Filter by status: pending/paid/expired."},
  {path:"/users",             comp:"UsersPage",        shell:"SAShell",        auth:"sa_only", note:"[ROADMAP] Search users, view profile, ban/unban, change role."},
  {path:"/app-config",        comp:"AppConfigPage",    shell:"SAShell",        auth:"sa_only", note:"[ROADMAP] Edit zinihubConfig constants per app. Stored S3."},
  {path:"/feature-flags",     comp:"FeatureFlagsPage", shell:"SAShell",        auth:"sa_only", note:"[ROADMAP] Toggle features per app or per hub."},
  {path:"/audit-log",         comp:"AuditLogPage",     shell:"SAShell",        auth:"sa_only", note:"[ROADMAP] Action history: actor · target · action · payload · timestamp."},
];

const SA_IA = {
  screens:[
    {id:"auth",    label:"Auth",           icon:"🔐",note:"SAPublicShell. Not in nav.",screens:[{id:"sa-login",label:"Login",tag:"public"}]},
    {id:"sub",     label:"Subscriptions",  icon:"💳",screens:[{id:"subscriptions",label:"Hub Table",tag:"core"},{id:"modal-extend",label:"Extend Validity Modal",tag:"modal"},{id:"modal-pay",label:"Payment Link Modal",tag:"modal"},{id:"modal-plan",label:"Change Plan Modal",tag:"modal"},{id:"modal-reset",label:"Reset PW Modal",tag:"modal"}]},
    {id:"plans",   label:"Plan Manager",   icon:"📦",screens:[{id:"plan-manager",label:"Plan Cards",tag:"core"},{id:"plan-edit-modal",label:"Edit Plan Modal",tag:"modal"}]},
    {id:"cashfree",label:"Cashfree Links", icon:"💸",screens:[{id:"cashfree-form",label:"Generate Form",tag:"core"},{id:"cashfree-generated",label:"Link Generated State",tag:"state"},{id:"cashfree-history",label:"Link History",tag:"core"}]},
    {id:"users",   label:"Users",          icon:"👥",note:"Roadmap",screens:[{id:"users-list",label:"User Search",tag:"roadmap"}]},
    {id:"config",  label:"App Config",     icon:"⚙️", note:"Roadmap",screens:[{id:"app-config",label:"Config Editor",tag:"roadmap"}]},
    {id:"flags",   label:"Feature Flags",  icon:"🚩",note:"Roadmap",screens:[{id:"feature-flags",label:"Flag Toggles",tag:"roadmap"}]},
    {id:"audit",   label:"Audit Log",      icon:"📋",note:"Roadmap",screens:[{id:"audit-log",label:"Action History",tag:"roadmap"}]},
  ],
};

const FOLDER_STRUCTURE = {
  root:"ziniz-super-admin/",
  tree:[
    {type:"dir",name:"src/",children:[
      {type:"dir",name:"config/",children:[
        {type:"file",name:"superAdminConfig.js",note:"SUPER_ADMIN_CONFIG — tour, cashfree env, subscription options, app registry"},
        {type:"file",name:"brandTokens.js",note:"T object (shared with ZiniHub — exact copy)"},
        {type:"file",name:"tourSteps.js",note:"TOUR_STEPS array for easy content editing"},
      ]},
      {type:"dir",name:"data/",children:[
        {type:"file",name:"mockPlans.js",note:"MOCK_PLANS — replace with API call"},
        {type:"file",name:"mockHubs.js",note:"MOCK_HUBS — replace with API call"},
      ]},
      {type:"dir",name:"hooks/",children:[
        {type:"file",name:"useTheme.js",note:"useTheme(dark) → th object"},
        {type:"file",name:"useTour.js",note:"tour state, step navigation, localStorage check"},
        {type:"file",name:"useActionToast.js",note:"toast queue with auto-dismiss"},
      ]},
      {type:"dir",name:"components/",children:[
        {type:"file",name:"Modal.jsx",note:"position:absolute modal — NOT fixed (iframe safe)"},
        {type:"file",name:"TourOverlay.jsx",note:"position:fixed + Canvas 2D spotlight (evenodd clip-path)"},
        {type:"file",name:"ActionMenu.jsx",note:"⋯ dropdown with mousedown outside-click close"},
        {type:"file",name:"AppSwitcher.jsx",note:"sidebar app selector — always visible"},
        {type:"file",name:"StatusBadge.jsx",note:"active/expiring/expired badge"},
        {type:"file",name:"PlanBadge.jsx",note:"trial/starter/professional/enterprise badge"},
        {type:"file",name:"FInput.jsx",note:"themed input — bg switches dark/light"},
        {type:"file",name:"FSelect.jsx",note:"themed select — bg switches dark/light (NOT #1e2330 hardcoded)"},
        {type:"file",name:"InfoStrip.jsx",note:"brand-tinted info row inside modals"},
        {type:"file",name:"WarnStrip.jsx",note:"amber warning row inside modals"},
      ]},
      {type:"dir",name:"modules/",children:[
        {type:"dir",name:"subscriptions/",children:[
          {type:"file",name:"SubscriptionsModule.jsx",note:"Hub table, search, filter, stats row"},
          {type:"file",name:"ExtendModal.jsx",note:"Days input → new expiry preview → confirm"},
          {type:"file",name:"PayLinkModal.jsx",note:"Plan+validity picker → amount → generate"},
          {type:"file",name:"ChangePlanModal.jsx",note:"Plan+validity → warning → confirm"},
          {type:"file",name:"ResetPWModal.jsx",note:"Show mobile → confirm → SMS OTP"},
        ]},
        {type:"dir",name:"plans/",children:[
          {type:"file",name:"PlanManagerModule.jsx",note:"Plan cards grid with validity toggle"},
          {type:"file",name:"PlanEditModal.jsx",note:"Name + pricing (3) + limits (num+bool)"},
        ]},
        {type:"dir",name:"cashfree/",children:[
          {type:"file",name:"CashfreeModule.jsx",note:"Hub search → plan/validity → amount → generate → copy/WhatsApp"},
          {type:"file",name:"CashfreeHistory.jsx",note:"Link history table with status filter"},
          {type:"file",name:"cashfreeService.js",note:"API proxy calls — NEVER expose x-client-secret to frontend"},
        ]},
        {type:"dir",name:"brand-guide/",children:[
          {type:"file",name:"BrandGuideModule.jsx",note:"Colors · Tokens · IA · Flows · Breakpoints · Config · Specs"},
        ]},
      ]},
      {type:"dir",name:"shell/",children:[
        {type:"file",name:"SAShell.jsx",note:"Main layout: Sidebar + Topbar + Content area"},
        {type:"file",name:"SAPublicShell.jsx",note:"Login shell: split left-brand / right-form"},
        {type:"file",name:"Sidebar.jsx",note:"210px/58px collapsed + AppSwitcher + NAV_ITEMS + bottom bar"},
        {type:"file",name:"Topbar.jsx",note:"50px · breadcrumb · role badge · SA avatar"},
      ]},
      {type:"dir",name:"pages/",children:[
        {type:"file",name:"LoginPage.jsx",note:"/login — public"},
        {type:"file",name:"SubscriptionsPage.jsx",note:"/subscriptions — sa_only"},
        {type:"file",name:"PlanManagerPage.jsx",note:"/plans — sa_only"},
        {type:"file",name:"CashfreeLinksPage.jsx",note:"/cashfree — sa_only"},
        {type:"file",name:"CashfreeHistoryPage.jsx",note:"/cashfree/history — sa_only"},
        {type:"file",name:"UsersPage.jsx",note:"/users — roadmap"},
        {type:"file",name:"AppConfigPage.jsx",note:"/app-config — roadmap"},
        {type:"file",name:"FeatureFlagsPage.jsx",note:"/feature-flags — roadmap"},
        {type:"file",name:"AuditLogPage.jsx",note:"/audit-log — roadmap"},
      ]},
      {type:"dir",name:"router/",children:[
        {type:"file",name:"AppRouter.jsx",note:"React Router v6. SARoute wrapper checks super_admin role."},
        {type:"file",name:"SARoute.jsx",note:"Protected route: if !isSuperAdmin → redirect /login"},
      ]},
      {type:"file",name:"App.jsx",note:"Root: ThemeProvider + Router + TourOverlay mount"},
      {type:"file",name:"main.jsx",note:"Vite entry"},
    ]},
    {type:"file",name:"index.html",note:"Vite HTML shell"},
    {type:"file",name:"vite.config.js",note:""},
    {type:"file",name:".env.example",note:"VITE_CASHFREE_BACKEND_URL — backend proxy URL only. Never CASHFREE_SECRET here."},
  ],
};

// Screen specs — component-level, exactly as zinihub pattern
const SCREEN_SPECS = [
  {
    id:"sa-login",
    screen:"Login Page",route:"/login",shell:"SAPublicShell",auth:"public",
    desc:"Split layout login. Left: brand panel. Right: form. Mobile: centred card with bolt icon.",
    mockId:"sa-login",
    fields:[
      {field:"Mobile / Username",type:"text",req:true,hint:"WhatsApp number or auto-assigned username"},
      {field:"Password",type:"password",req:true,hint:""},
    ],
    states:[
      {s:"Default",n:"Empty form, Login CTA disabled"},
      {s:"Filled",n:"CTA enabled — brand blue"},
      {s:"Loading",n:"CTA shows spinner, disabled"},
      {s:"Error",n:"Red inline error below password: 'Invalid credentials'"},
      {s:"No role",n:"Error: 'Access denied — super_admin role required'"},
    ],
    tokens:["Left panel: gradient 135deg brand→violet","Left panel width: 42%","Form maxWidth: 280px","Role check: user.roles.includes('super_admin')"],
  },
  {
    id:"subscriptions",
    screen:"Subscriptions",route:"/subscriptions",shell:"SAShell",auth:"sa_only",
    desc:"Hub table with search, status filter, stats cards. All subscription actions via ⋯ dropdown.",
    mockId:"subscriptions",
    fields:[
      {field:"Search",type:"text",req:false,hint:"Hub name / owner name / mobile number — real-time filter"},
      {field:"Status filter",type:"select",req:false,options:["All Status","Active","Expiring Soon","Expired"]},
    ],
    states:[
      {s:"Loading",n:"Table skeleton shimmer"},
      {s:"Empty search",n:"Inline: 'No hubs match — try a different name'"},
      {s:"Table populated",n:"Row: Hub name+ID · Owner+mobile · PlanBadge · Validity · Expiry+days · StatusBadge · ⋯"},
      {s:"Row ⋯ open",n:"Dropdown: Extend validity · Generate pay link · Change plan · Reset password"},
    ],
    tokens:["Stats: 4-col grid, gap 12px, 28px/700 value","Table header: muted bg, 11px/600 uppercase, letter-spacing 0.04em","Expiry color: red <0d · amber <14d · muted else","⋯ active: brandSoft bg + brandBorder"],
  },
  {
    id:"extend-modal",
    screen:"Extend Validity Modal",route:"(modal)",shell:"SAShell",auth:"sa_only",
    desc:"Opens from ⋯ → Extend validity. Extends existing expiryDate by N days (NOT today + N).",
    mockId:"modal-extend",
    fields:[
      {field:"Days to extend",type:"number",req:true,hint:"Max 365 — configurable in SUPER_ADMIN_CONFIG.SUBSCRIPTION.MAX_EXTENSION_DAYS"},
    ],
    states:[
      {s:"Open",n:"Shows current plan + expiry + StatusBadge in InfoStrip"},
      {s:"Valid input",n:"New expiry preview updates live below input"},
      {s:"Invalid (> max / negative)",n:"Red border on input + 'Max 365 days' error"},
      {s:"Confirm",n:"API PATCH /hubs/{id}/subscription → success toast → modal closes → table refreshes"},
      {s:"API error",n:"Error in WarnStrip inside modal — do not close"},
    ],
    tokens:["Modal bg: #2C3149 dark / #FFFFFF light","Modal border-left: 3px solid brand","InfoStrip: rgba(94,76,226,0.12) bg","New expiry: computed client-side, 14px/600 white"],
  },
  {
    id:"cashfree-form",
    screen:"Cashfree — Generate Link Form",route:"/cashfree",shell:"SAShell",auth:"sa_only",
    desc:"3-section form: hub selection → plan+validity → customer details. Generates Cashfree payment link via backend proxy.",
    mockId:"cashfree-form",
    fields:[
      {field:"Hub search",type:"text (autocomplete)",req:true,hint:"Search by name/owner — shows mobile + current plan on selection"},
      {field:"Plan",type:"select",req:true,options:["Starter","Professional","Enterprise"]},
      {field:"Validity",type:"select",req:true,options:["Monthly","Quarterly","Annual"]},
      {field:"Customer name",type:"text (auto-filled)",req:true,hint:"Pre-filled from hub owner. Editable."},
      {field:"Customer mobile",type:"text (auto-filled)",req:true,hint:"Pre-filled from hub record. Editable."},
      {field:"Customer email",type:"email",req:false,hint:"Optional — for payment receipt from Cashfree"},
      {field:"Link description",type:"text (auto-filled)",req:false,hint:"Auto: 'ZiniHub {Plan} – {Validity}'"},
      {field:"Link expiry",type:"select",req:false,options:["24 hours","48 hours","7 days"],hint:"Default: SUPER_ADMIN_CONFIG.CASHFREE.LINK_EXPIRY_MINUTES"},
    ],
    states:[
      {s:"Step 1",n:"Hub not selected — plan/validity/customer fields disabled"},
      {s:"Hub selected",n:"Customer details auto-fill · amount preview shows · form fully active"},
      {s:"Generating",n:"CTA spinner · disabled · 'Generating…'"},
      {s:"Success",n:"Green success card with link URL + Copy + WhatsApp + New Link CTAs"},
      {s:"API error",n:"Red error card: 'Cashfree API error: {message}' — try again"},
    ],
    tokens:["3 sections: each surface card + border + radius rlg + padding 10px 12px","Section label: 9px/600 uppercase muted + numbered (1·, 2·, 3·)","Amount preview: brandSoft bg + brandBorder border + 16px/700 brand amount","Success card: greenSoft bg + green border + monospace link URL"],
    note:"API call goes to YOUR BACKEND (not directly to Cashfree). Backend holds x-client-id + x-client-secret. Frontend sends: { hubId, plan, validity, customer, description, expiryMinutes }.",
  },
  {
    id:"cashfree-generated",
    screen:"Cashfree — Link Generated State",route:"/cashfree (success state)",shell:"SAShell",auth:"sa_only",
    desc:"Shown after successful generation. Link URL, copy button, WhatsApp deep link, history of recent links for this hub.",
    mockId:"cashfree-generated",
    fields:[],
    states:[
      {s:"Success",n:"Green card with URL + Copy + WhatsApp + New Link"},
      {s:"Copy clicked",n:"Button changes to '✓ Copied' for 2 seconds then resets"},
      {s:"WhatsApp",n:"Opens wa.me/?text=... with pre-filled message: 'Your ZiniHub payment link: {url}'"},
      {s:"New Link",n:"Resets form back to Step 1 (hub pre-selected)"},
      {s:"History section",n:"Last 5 links for this hub: desc · time · status badge (pending/paid/expired)"},
    ],
    tokens:["URL display: monospace 8px, cyan, wordBreak break-all","History row: flex · desc flex-1 · time muted · badge","Paid badge: greenSoft","Pending badge: amberSoft","Expired badge: redSoft"],
  },
  {
    id:"plan-manager",
    screen:"Plan Manager",route:"/plans",shell:"SAShell",auth:"sa_only",
    desc:"4 plan cards with live pricing preview by validity. Edit any plan via modal. New plan via + button.",
    mockId:"plan-manager",
    fields:[
      {field:"Validity toggle",type:"pill tabs",req:false,options:["Monthly","Quarterly","Annual"],hint:"Switches displayed price on all cards simultaneously"},
    ],
    states:[
      {s:"Default",n:"4 cards: Trial·Starter·Professional·Enterprise in 4-col grid"},
      {s:"Edit clicked",n:"PlanEditModal opens with current values pre-filled"},
      {s:"New Plan",n:"PlanEditModal opens with blank defaults"},
      {s:"Save",n:"Mock: local state update + toast 'Saved → propagating to DB/S3'"},
    ],
    tokens:["Card border-top: 3px solid plan.color","Price: 22px/700 primary + /validity 11px/400 muted","Limit rows: 12px · justify space-between","Bool: green ✓ / red ✗","Unlimited (-1): ∞"],
  },
  {
    id:"plan-edit-modal",
    screen:"Plan Edit Modal",route:"(modal from /plans)",shell:"SAShell",auth:"sa_only",
    desc:"Edit plan name, pricing (all 3 validities), and all feature limits. Bool limits use toggle switches.",
    mockId:"plan-edit-modal",
    fields:[
      {field:"Plan Name",type:"text",req:true,hint:""},
      {field:"Price Monthly",type:"number",req:true,hint:"₹ INR · 0 for free plans"},
      {field:"Price Quarterly",type:"number",req:true,hint:"₹ INR"},
      {field:"Price Annual",type:"number",req:true,hint:"₹ INR"},
      {field:"Max Stores",type:"number",req:true,hint:"-1 = unlimited"},
      {field:"Max Items / Store",type:"number",req:true,hint:"-1 = unlimited"},
      {field:"Max Images / Item",type:"number",req:true,hint:""},
      {field:"Max Staff Accounts",type:"number",req:true,hint:"-1 = unlimited"},
      {field:"Media Storage (MB)",type:"number",req:true,hint:""},
      {field:"KYC Module",type:"toggle",req:false,hint:""},
      {field:"Orders Module",type:"toggle",req:false,hint:""},
      {field:"Analytics Module",type:"toggle",req:false,hint:""},
    ],
    states:[
      {s:"Open",n:"Pre-filled with current plan values"},
      {s:"Valid",n:"Save Plan CTA active"},
      {s:"Save",n:"PATCH /plans/{id} → success toast → modal closes → cards refresh"},
    ],
    tokens:["Toggle: width 44px height 24px · active: green · inactive: #334155","Number inputs: width 72px · text-align right","Tip: 'Use -1 for unlimited' below limits section","Pricing grid: 3 equal columns with validity labels"],
  },
];

// ─── SUPER ADMIN CONFIG (shown in brand/config section) ────────────────────────
const SA_CONFIG_KEYS = [
  {key:"TOUR.ENABLED",type:"bool","default":"true",desc:"Master switch for tour feature"},
  {key:"TOUR.IDLE_DAYS_TRIGGER",type:"number","default":"30",desc:"Days of inactivity before tour auto-shows again"},
  {key:"TOUR.VERSION",type:"string","default":"'1.0.0'",desc:"Bump to reset all users' tour-seen state (changes localStorage key suffix)"},
  {key:"CASHFREE.ENV",type:"enum","default":"'sandbox'",desc:"'sandbox' | 'production' — controls which Cashfree URL is used"},
  {key:"CASHFREE.LINK_EXPIRY_MINUTES",type:"number","default":"1440",desc:"Default payment link TTL (24h). User can override per link."},
  {key:"CASHFREE.CURRENCY",type:"string","default":"'INR'",desc:"Cashfree currency code"},
  {key:"SUBSCRIPTION.MAX_EXTENSION_DAYS",type:"number","default":"365",desc:"Hard cap on extend validity input"},
  {key:"SUBSCRIPTION.DEFAULT_EXTENSION_DAYS",type:"number","default":"30",desc:"Pre-filled value in extend modal"},
  {key:"SUBSCRIPTION.VALIDITY_OPTIONS",type:"string[]","default":"['monthly','quarterly','annual']",desc:"Shown in plan pickers and validity toggles"},
  {key:"APPS",type:"AppDef[]","default":"[zinihub, zinishop, zinibook]",desc:"App registry. disabled:true hides from switcher interactions."},
];

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function SuperAdminUXWizard(){
  return (
    <InspectProvider>
      <SuperAdminUXWizardInner />
    </InspectProvider>
  );
}

function SuperAdminUXWizardInner(){
  const [dark,setDark]=useState(true);
  const [section,setSection]=useState("flows");
  const [viewport,setViewport]=useState("desktop");
  const [sidebarOpen,setSidebarOpen]=useState(false);
  const [expandedGroup,setExpandedGroup]=useState("sub");
  const [activeSpec,setActiveSpec]=useState("sa-login");
  const [folderExpanded,setFolderExpanded]=useState({});
  const [showSpec,setShowSpec]=useState(true);
  const th=useTheme(dark);

  const SECTIONS = WIZARD_TABS;

  const flowSidebarContent = (
    <>
      {SA_IA.screens.map(group => (
        <div key={group.id}>
          <div
            onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', cursor: 'pointer', background: expandedGroup === group.id ? T.brandSoft : 'transparent', borderLeft: expandedGroup === group.id ? `2px solid ${T.brand}` : '2px solid transparent' }}
          >
            <span style={{ fontSize: 13 }}>{group.icon}</span>
            <span style={{ fontSize: 11, color: expandedGroup === group.id ? T.brand : th.text, fontWeight: expandedGroup === group.id ? '500' : '400', flex: 1 }}>{group.label}</span>
            {group.note && <span style={{ fontSize: 8, color: th.textMuted, background: T.amberSoft, borderRadius: 3, padding: '1px 4px' }}>soon</span>}
          </div>
          {expandedGroup === group.id && group.screens.map(sc => (
            <div key={sc.id} onClick={() => setActiveSpec(sc.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px 4px 30px', cursor: 'pointer', background: activeSpec === sc.id ? T.brandSoft : 'transparent', fontSize: 10, color: activeSpec === sc.id ? T.brand : th.textSec }}>
              {sc.bug && <span style={{ fontSize: 8, background: T.redSoft, color: T.red, borderRadius: 3, padding: '1px 4px' }}>bug</span>}
              <span style={{ flex: 1 }}>{sc.label}</span>
              <span style={{ fontSize: 8, color: th.textMuted, background: th.muted, borderRadius: 3, padding: '1px 4px' }}>{sc.tag}</span>
            </div>
          ))}
        </div>
      ))}
    </>
  );

  // Folder tree renderer
  function FolderNode({node,depth=0}){
    const key=node.name+(depth);
    const expanded=folderExpanded[key]!==false;
    const isDir=node.type==="dir";
    return(
      <div>
        <div onClick={()=>isDir&&setFolderExpanded(e=>({...e,[key]:!expanded}))}
          style={{display:"flex",alignItems:"flex-start",gap:6,padding:"2px 0 2px"+(depth*12)+"px",cursor:isDir?"pointer":"default",paddingLeft:depth*14+4}}>
          <span style={{fontSize:10,color:isDir?T.amber:T.cyan,flexShrink:0,fontFamily:"monospace"}}>{isDir?(expanded?"▾ ":"▸ "):"  "}{node.name}</span>
          {node.note&&<span style={{fontSize:9,color:th.textMuted,lineHeight:1.4,marginTop:1}}>{node.note}</span>}
        </div>
        {isDir&&expanded&&node.children&&node.children.map((child,i)=>(
          <FolderNode key={child.name+i} node={child} depth={depth+1}/>
        ))}
      </div>
    );
  }

  return(
    <WizardChrome
      dark={dark}
      setDark={setDark}
      title="ZiniZ Super Admin"
      subtitle="Design Wizard v1"
      domain="superadmin.ziniz.online"
      sections={SECTIONS}
      section={section}
      onSectionChange={setSection}
      viewport={viewport}
      onViewportChange={setViewport}
      sidebarOpen={sidebarOpen}
      onSidebarToggle={()=>setSidebarOpen(value=>!value)}
      flowSidebar={section==="flows"?flowSidebarContent:null}
      inspectorOpen={false}
    >
      <div style={{marginBottom:12,fontSize:11,color:th.textMuted}}>
        Brand tokens: <Link to="/brand-book" style={{color:T.brand}}>Brand Book →</Link>
      </div>

          {/* SCREENS */}
          {section==="flows"&&(()=>{
            const spec=SCREEN_SPECS.find(s=>s.id===activeSpec)||SCREEN_SPECS[0];
            return(
              <div style={{maxWidth:960}}>
                <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:4}}>
                  <span style={{fontFamily:"monospace",fontSize:11,color:T.cyan}}>{spec.route}</span>
                  <span style={{fontSize:9,background:spec.auth==="sa_only"?T.brandSoft:T.greenSoft,color:spec.auth==="sa_only"?T.brand:T.green,borderRadius:4,padding:"1px 6px",border:`1px solid ${spec.auth==="sa_only"?T.brandBorder:"rgba(34,197,94,0.3)"}`}}>{spec.auth}</span>
                  <span style={{fontSize:9,color:th.textMuted,background:th.muted,borderRadius:4,padding:"1px 6px"}}>{spec.shell}</span>
                </div>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:4}}>
                  <div style={{fontSize:18,fontWeight:600,color:th.text}}>{spec.screen}</div>
                  <button onClick={()=>setShowSpec(v=>!v)} style={{background:showSpec?T.brandSoft:th.muted,border:`1px solid ${showSpec?T.brandBorder:th.border}`,borderRadius:T.rmd,padding:"4px 10px",cursor:"pointer",color:showSpec?T.brand:th.textSec,fontSize:11,fontWeight:500,transition:th.tr,flexShrink:0,marginTop:2}}>
                    {showSpec?"Hide Specs ↗":"Show Specs ↙"}
                  </button>
                </div>
                <div style={{fontSize:12,color:th.textSec,marginBottom:16,lineHeight:1.6}}>{spec.desc}</div>

                <div style={{display:"grid",gridTemplateColumns:showSpec?"1fr 360px":"1fr",gap:16,alignItems:"start",transition:`grid-template-columns ${T.mid} ${T.ease}`}}>
                  {/* Left: wireframe */}
                  <div>
                    <div style={{fontSize:10,color:th.textMuted,marginBottom:6,fontWeight:500}}>Wireframe · {viewport}</div>
                    <ViewportFrame viewport={viewport} dark={dark} minCanvasHeight={canvasHeightForViewport(viewport)}>
                      <ScreenWF id={spec.mockId} dark={dark} viewport={viewport} slim={showSpec}/>
                    </ViewportFrame>
                    {spec.note&&<div style={{marginTop:8,padding:"7px 10px",background:T.amberSoft,borderRadius:T.rmd,border:`1px solid ${T.amber}33`,fontSize:10,color:T.amber}}>⚠ {spec.note}</div>}
                  </div>

                  {/* Right: spec panels — hidden when showSpec=false */}
                  {showSpec&&<div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {/* Fields */}
                    {spec.fields.length>0&&(
                      <div style={{background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,overflow:"hidden"}}>
                        <div style={{padding:"7px 12px",borderBottom:`1px solid ${th.border}`,fontSize:10,fontWeight:600,color:th.textSec,textTransform:"uppercase",letterSpacing:"0.06em",background:th.muted}}>Fields</div>
                        {spec.fields.map((f,i)=>(
                          <div key={f.field} style={{padding:"8px 12px",borderBottom:i<spec.fields.length-1?`1px solid ${th.border}`:"none"}}>
                            <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:2}}>
                              {f.req&&<span style={{fontSize:8,color:T.red}}>*</span>}
                              <span style={{fontSize:11,fontWeight:500,color:th.text}}>{f.field}</span>
                              <span style={{fontFamily:"monospace",fontSize:9,color:T.cyan,marginLeft:"auto"}}>{f.type}</span>
                            </div>
                            {f.options&&<div style={{display:"flex",gap:3,flexWrap:"wrap",marginTop:3}}>{f.options.map(o=><span key={o} style={{fontSize:9,background:th.muted,borderRadius:3,padding:"1px 5px",color:th.textSec}}>{o}</span>)}</div>}
                            {f.hint&&<div style={{fontSize:9,color:th.textMuted,marginTop:2}}>{f.hint}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* States */}
                    <div style={{background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,overflow:"hidden"}}>
                      <div style={{padding:"7px 12px",borderBottom:`1px solid ${th.border}`,fontSize:10,fontWeight:600,color:th.textSec,textTransform:"uppercase",letterSpacing:"0.06em",background:th.muted}}>States</div>
                      {spec.states.map((st,i)=>(
                        <div key={st.s} style={{padding:"7px 12px",borderBottom:i<spec.states.length-1?`1px solid ${th.border}`:"none",display:"flex",gap:8,alignItems:"flex-start"}}>
                          <span style={{fontSize:9,fontWeight:600,color:T.brand,background:T.brandSoft,borderRadius:3,padding:"1px 5px",flexShrink:0,marginTop:1}}>{st.s}</span>
                          <span style={{fontSize:10,color:th.textSec,lineHeight:1.5}}>{st.n}</span>
                        </div>
                      ))}
                    </div>
                    {/* Tokens */}
                    <div style={{background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,overflow:"hidden"}}>
                      <div style={{padding:"7px 12px",borderBottom:`1px solid ${th.border}`,fontSize:10,fontWeight:600,color:th.textSec,textTransform:"uppercase",letterSpacing:"0.06em",background:th.muted}}>Tokens / Implementation</div>
                      <div style={{padding:"8px 12px"}}>
                        {spec.tokens.map((tk,i)=><div key={i} style={{fontSize:10,color:T.cyan,fontFamily:"monospace",padding:"2px 0"}}>→ {tk}</div>)}
                      </div>
                    </div>
                  </div>}
                </div>
              </div>
            );
          })()}

          {/* IA */}
          {section==="ia"&&(
            <div style={{maxWidth:760}}>
              <div style={{fontSize:18,fontWeight:600,marginBottom:6}}>Information Architecture</div>
              <div style={{color:th.textSec,fontSize:12,marginBottom:20}}>All screens for the ZiniZ Super Admin app.</div>
              <div style={{background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"180px 100px 1fr",gap:0,padding:"6px 14px",background:th.muted,fontSize:9,fontWeight:600,color:th.textMuted,textTransform:"uppercase",letterSpacing:"0.07em"}}>
                  {["Module · Screen","Tag","Notes"].map(h=><div key={h}>{h}</div>)}
                </div>
                {SA_IA.screens.map(group=>(
                  <React.Fragment key={group.id}>
                    <div style={{display:"grid",gridTemplateColumns:"180px 100px 1fr",gap:0,padding:"7px 14px",background:dark?"rgba(94,76,226,0.06)":"rgba(94,76,226,0.04)",borderBottom:`1px solid ${th.border}`,borderTop:`1px solid ${th.border}`}}>
                      <div style={{display:"flex",alignItems:"center",gap:6}}><span>{group.icon}</span><span style={{fontSize:11,fontWeight:600,color:T.brand}}>{group.label}</span></div>
                      <div>{group.note&&<span style={{fontSize:8,background:T.amberSoft,color:T.amber,borderRadius:3,padding:"1px 5px"}}>roadmap</span>}</div>
                      <div style={{fontSize:9,color:th.textMuted,fontStyle:"italic"}}>{group.note||""}</div>
                    </div>
                    {group.screens.map((sc,i)=>(
                      <div key={sc.id} style={{display:"grid",gridTemplateColumns:"180px 100px 1fr",gap:0,padding:"6px 14px 6px 28px",borderBottom:i<group.screens.length-1?`1px solid ${th.border}`:"none",alignItems:"center"}}>
                        <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:th.textSec}}>
                          {sc.bug&&<span style={{fontSize:8,background:T.redSoft,color:T.red,borderRadius:3,padding:"1px 4px"}}>bug</span>}
                          {sc.label}
                        </div>
                        <span style={{fontSize:9,background:th.muted,color:th.textMuted,borderRadius:3,padding:"1px 5px",width:"fit-content"}}>{sc.tag}</span>
                        <div style={{fontSize:9,color:th.textMuted,fontFamily:"monospace"}}>{sc.id}</div>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* ROUTES */}
          {section==="routes"&&(
            <div style={{maxWidth:960}}>
              <div style={{fontSize:18,fontWeight:600,marginBottom:6}}>Route Map</div>
              <div style={{color:th.textSec,fontSize:12,marginBottom:4}}>React Router v6. All protected routes check <span style={{fontFamily:"monospace",color:T.cyan}}>user.roles.includes('super_admin')</span> via <span style={{fontFamily:"monospace",color:T.cyan}}>SARoute</span> wrapper.</div>
              <div style={{fontSize:11,color:T.amber,background:T.amberSoft,borderRadius:T.rmd,padding:"6px 10px",marginBottom:18,border:`1px solid ${T.amber}33`}}>⚠ Auth is role-based on existing ZiniHub accounts. No separate user table — add <span style={{fontFamily:"monospace"}}>super_admin</span> to a ZiniHub user's roles array.</div>
              <div style={{background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"160px 160px 100px 70px 1fr",gap:0,padding:"6px 14px",background:th.muted,fontSize:9,fontWeight:600,color:th.textMuted,textTransform:"uppercase",letterSpacing:"0.07em"}}>
                  {["Path","Component","Shell","Auth","Notes"].map(h=><div key={h}>{h}</div>)}
                </div>
                {SA_ROUTES.map((r,i)=>(
                  <div key={r.path} style={{display:"grid",gridTemplateColumns:"160px 160px 100px 70px 1fr",gap:0,padding:"8px 14px",borderBottom:i<SA_ROUTES.length-1?`1px solid ${th.border}`:"none",alignItems:"start"}}>
                    <div style={{fontFamily:"monospace",fontSize:10,color:T.cyan}}>{r.path}</div>
                    <div style={{fontFamily:"monospace",fontSize:10,color:T.brand}}>{r.comp}</div>
                    <div style={{fontSize:10,color:th.textSec}}>{r.shell}</div>
                    <span style={{fontSize:9,background:r.auth==="sa_only"?T.brandSoft:T.greenSoft,color:r.auth==="sa_only"?T.brand:T.green,borderRadius:4,padding:"1px 5px",width:"fit-content",border:`1px solid ${r.auth==="sa_only"?T.brandBorder:"rgba(34,197,94,0.3)"}`}}>{r.auth}</span>
                    <div style={{fontSize:10,color:th.textMuted,lineHeight:1.5}}>{r.note}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FOLDER */}
          {section==="folders"&&(
            <div style={{maxWidth:860}}>
              <div style={{fontSize:18,fontWeight:600,marginBottom:6}}>Folder Structure</div>
              <div style={{color:th.textSec,fontSize:12,marginBottom:20}}>Production-ready project layout. Click directories to expand/collapse.</div>
              <div style={{background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,padding:"16px 20px",fontFamily:"monospace"}}>
                <div style={{fontSize:11,color:T.amber,fontWeight:600,marginBottom:10}}>📁 {FOLDER_STRUCTURE.root}</div>
                {FOLDER_STRUCTURE.tree.map((node,i)=><FolderNode key={node.name+i} node={node} depth={1}/>)}
              </div>
              <div style={{marginTop:16,background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,padding:"14px 16px"}}>
                <div style={{fontSize:12,fontWeight:600,color:th.text,marginBottom:10}}>Critical File Rules</div>
                {[
                  [".env.example","VITE_CASHFREE_BACKEND_URL only. NEVER VITE_CASHFREE_SECRET — never expose Cashfree credentials to frontend.",T.red],
                  ["cashfreeService.js","Only calls YOUR backend proxy. Backend holds x-client-id + x-client-secret.",T.amber],
                  ["FSelect.jsx","background must be dark?T.dMuted:T.lSurface — NEVER hardcoded #1e2330 (renders black in light mode).",T.coral],
                  ["TourOverlay.jsx","position:fixed + Canvas 2D evenodd clip-path. canvas.width=window.innerWidth explicitly (100% doesn't work in fixed context).",T.brand],
                  ["Modal.jsx","position:absolute on content pane — NOT position:fixed (iframe/sandbox collapses viewport).",T.cyan],
                ].map(([file,note,col])=>(
                  <div key={file} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:`1px solid ${th.border}`,fontSize:11}}>
                    <span style={{fontFamily:"monospace",color:col,width:160,flexShrink:0}}>{file}</span>
                    <span style={{color:th.textSec,lineHeight:1.5}}>{note}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SHELLS */}
          {section==="shells"&&(
            <div style={{maxWidth:760}}>
              <div style={{fontSize:18,fontWeight:600,marginBottom:6}}>SAShell</div>
              <div style={{color:th.textSec,fontSize:12,marginBottom:16}}>superadmin.ziniz.online — Brand at <Link to="/brand-book" style={{color:T.brand}}>/brand-book</Link></div>
              <div style={{background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,padding:"14px 16px",fontSize:11,lineHeight:1.7,color:th.textSec}}>
                <div><strong style={{color:th.text}}>Topbar:</strong> logo · breadcrumb · Super Admin badge · avatar</div>
                <div style={{marginTop:8}}><strong style={{color:th.text}}>Sidebar:</strong> App switcher · Subscriptions · Plan Manager · roadmap modules</div>
              </div>
            </div>
          )}

          {/* TECH STACK */}
          {section==="tech-stack"&&(
            <div style={{maxWidth:760}}>
              <div style={{fontSize:18,fontWeight:600,marginBottom:6}}>Tech Stack</div>
              <div style={{background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,overflow:"hidden"}}>
                {[["Frontend","React 19 · CRA · Router 7 · Ant Design 5"],["Backend","AWS SAM · Node 22 · Lambda"],["Data","DynamoDB · S3"],["Payments","Cashfree backend proxy"],["Auth","ZiniHub + super_admin role"]].map(([layer,stack],index,arr)=>(
                  <div key={layer} style={{display:"grid",gridTemplateColumns:"120px 1fr",padding:"10px 14px",borderBottom:index<arr.length-1?`1px solid ${th.border}`:"none"}}>
                    <div style={{fontSize:11,fontWeight:600,color:T.brand}}>{layer}</div>
                    <div style={{fontSize:11,color:th.textSec}}>{stack}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CONFIGS */}
          {/* CONFIG */}
          {section==="configs"&&(
            <div style={{maxWidth:860}}>
              <div style={{fontSize:18,fontWeight:600,marginBottom:6}}>SUPER_ADMIN_CONFIG Reference</div>
              <div style={{color:th.textSec,fontSize:12,marginBottom:4}}>Single source of configurable defaults — equivalent of ZiniHub's zinihubConfig.js. Lives in <span style={{fontFamily:"monospace",color:T.cyan}}>src/config/superAdminConfig.js</span>.</div>
              <div style={{fontSize:11,color:T.brand,background:T.brandSoft,borderRadius:T.rmd,padding:"6px 10px",marginBottom:18,border:`1px solid ${T.brandBorder}`}}>Update this file to change behaviour without touching component code. Future: load overrides from DB/S3 and merge at runtime.</div>
              <div style={{background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,overflow:"hidden",marginBottom:20}}>
                <div style={{display:"grid",gridTemplateColumns:"200px 80px 120px 1fr",gap:0,padding:"6px 14px",background:th.muted,fontSize:9,fontWeight:600,color:th.textMuted,textTransform:"uppercase",letterSpacing:"0.07em"}}>
                  {["Key","Type","Default","Description"].map(h=><div key={h}>{h}</div>)}
                </div>
                {SA_CONFIG_KEYS.map((k,i)=>(
                  <div key={k.key} style={{display:"grid",gridTemplateColumns:"200px 80px 120px 1fr",gap:0,padding:"8px 14px",borderBottom:i<SA_CONFIG_KEYS.length-1?`1px solid ${th.border}`:"none",alignItems:"start"}}>
                    <div style={{fontFamily:"monospace",fontSize:10,color:T.cyan}}>{k.key}</div>
                    <div style={{fontFamily:"monospace",fontSize:9,color:T.amber}}>{k.type}</div>
                    <div style={{fontFamily:"monospace",fontSize:9,color:th.textSec}}>{k["default"]}</div>
                    <div style={{fontSize:10,color:th.textMuted,lineHeight:1.5}}>{k.desc}</div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:13,fontWeight:600,marginBottom:10}}>Data Models</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                {/* Hub model */}
                <div style={{background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,overflow:"hidden"}}>
                  <div style={{padding:"7px 12px",borderBottom:`1px solid ${th.border}`,fontSize:11,fontWeight:600,color:T.brand,background:T.brandSoft}}>Hub / Subscription</div>
                  {[["id","string","unique hub id e.g. 'hub22'"],["name","string","hub display name"],["owner","string","owner full name"],["mobile","string","+91... with country code"],["plan","enum","trial|starter|professional|enterprise"],["validity","enum","monthly|quarterly|annual"],["expiryDate","ISO date","server-computed"],["status","derived","expired/expiring_soon/active — computed client-side"],["app","string","'zinihub' | 'zinishop' etc"],].map(([f,t,n])=>(
                    <div key={f} style={{display:"grid",gridTemplateColumns:"90px 70px 1fr",gap:4,padding:"5px 12px",borderBottom:`1px solid ${th.border}`,fontSize:9}}>
                      <span style={{fontFamily:"monospace",color:T.cyan}}>{f}</span>
                      <span style={{fontFamily:"monospace",color:T.amber}}>{t}</span>
                      <span style={{color:th.textMuted}}>{n}</span>
                    </div>
                  ))}
                </div>
                {/* Plan model */}
                <div style={{background:th.surface,border:`1px solid ${th.border}`,borderRadius:T.rlg,overflow:"hidden"}}>
                  <div style={{padding:"7px 12px",borderBottom:`1px solid ${th.border}`,fontSize:11,fontWeight:600,color:T.violet,background:"rgba(124,58,237,0.1)"}}>Plan Definition</div>
                  {[["id","string","plan identifier"],["label","string","display name"],["price.monthly","number","INR"],["price.quarterly","number","INR"],["price.annual","number","INR"],["limits.maxStores","int","-1 = unlimited"],["limits.maxItemsPerStore","int","-1 = unlimited"],["limits.maxImagesPerItem","int",""],["limits.maxStaffAccounts","int","-1 = unlimited"],["limits.maxMediaStorageMB","int",""],["limits.enableKYC","bool","feature gate"],["limits.enableOrders","bool","feature gate"],["limits.enableAnalytics","bool","feature gate"],].map(([f,t,n])=>(
                    <div key={f} style={{display:"grid",gridTemplateColumns:"130px 50px 1fr",gap:4,padding:"5px 12px",borderBottom:`1px solid ${th.border}`,fontSize:9}}>
                      <span style={{fontFamily:"monospace",color:T.cyan}}>{f}</span>
                      <span style={{fontFamily:"monospace",color:T.amber}}>{t}</span>
                      <span style={{color:th.textMuted}}>{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
    </WizardChrome>
  );
}
